const LEGACY_VITE_FAVICON_PATH = '/vite.svg';

/**
 * The original Vite icon was persisted as a tenant setting before branding was
 * configurable. Treat it as an unset placeholder so existing tenants inherit
 * their own logo without requiring a data migration.
 */
export function isLegacyViteFavicon(url?: string | null): boolean {
  const value = url?.trim();
  if (!value) return false;

  try {
    return new URL(value, 'https://tenant.invalid').pathname === LEGACY_VITE_FAVICON_PATH;
  } catch {
    return value.split(/[?#]/, 1)[0] === LEGACY_VITE_FAVICON_PATH;
  }
}

export function resolveTenantFaviconUrl(
  faviconUrl?: string | null,
  logoUrl?: string | null
): string {
  const configuredFavicon = faviconUrl?.trim() || '';
  const configuredLogo = logoUrl?.trim() || '';
  const resolved =
    !configuredFavicon || isLegacyViteFavicon(configuredFavicon)
      ? configuredLogo
      : configuredFavicon;

  return resolved;
}

function faviconMimeType(url: string): string {
  let pathname = url;
  try {
    const parsed = new URL(url, window.location.origin);
    pathname = parsed.searchParams.get('key') || parsed.pathname;
  } catch {
    pathname = url.split(/[?#]/, 1)[0];
  }

  const extension = pathname.toLowerCase().split('.').pop();
  switch (extension) {
    case 'svg':
      return 'image/svg+xml';
    case 'ico':
      return 'image/x-icon';
    case 'webp':
      return 'image/webp';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    default:
      return 'image/png';
  }
}

export function applyTenantFavicon(url: string): void {
  if (typeof document === 'undefined') return;

  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!url) {
    const existingUrl = existing?.getAttribute('href');
    if (
      existing &&
      (existing.dataset.tenantFavicon === 'true' || isLegacyViteFavicon(existingUrl))
    ) {
      existing.remove();
    }
    return;
  }

  const favicon = existing || document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = faviconMimeType(url);
  favicon.href = url;
  favicon.dataset.tenantFavicon = 'true';

  if (!existing) {
    document.head.appendChild(favicon);
  }
}
